import { MutableRefObject, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

interface UseGsapTimelineParams {
  isDesktop: boolean;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  setActiveIndex: (index: number) => void;
}

/**
 * This custom hook abstracts the GSAP timeline and scroll trigger animations for the Timeline component.
 * It manages the animations based on the `isDesktop` flag and the provided refs,
 * setting specific animation defaults for ease and duration to ensure consistent animation behavior.
 *
 * @param {Object} params - Parameters for configuring the GSAP animations.
 * @param {boolean} params.isDesktop - Indicates if the current viewport is in desktop mode.
 * @param {React.MutableRefObject<(HTMLDivElement | null)>} params.containerRef - Ref to the timeline container.
 * @param {(index: number) => void} params.setActiveIndex - Function to update the active timeline item index.
 */

const useGsapTimeline = ({ isDesktop, containerRef, setActiveIndex }: UseGsapTimelineParams) => {
  const alreadyRecalculated = useRef(false);

  useGSAP(
    () => {
      if (!isDesktop) return;
      let tl: gsap.core.Timeline;

      const timelineItems = gsap.utils.toArray('.timeline-item');
      timelineItems.forEach((item, index) => {
        if (index === timelineItems.length - 1) return;

        const q = gsap.utils.selector(item as Element);

        tl = gsap.timeline({
          defaults: { ease: 'power2.Out' }
        });

        tl.fromTo(q('.progressline'), { height: '2%' }, { height: '128%', immediateRender: false });

        ScrollTrigger.create({
          id: `timeline-item-${index}`,
          trigger: item as Element,
          start: 'top+=150 center',
          end: 'bottom+=250 center',
          animation: tl,
          scrub: 1,
          // markers: true,
          preventOverlaps: true,
          fastScrollEnd: true,
          onLeave: () => {
            if (index + 1 > timelineItems.length) return;
            setActiveIndex(index + 1);
          },
          onEnterBack: () => setActiveIndex(index),
          onEnter: () => {
            if (alreadyRecalculated.current) return;
            ScrollTrigger.refresh();
            alreadyRecalculated.current = true;
          }
        });
      });
    },
    { dependencies: [isDesktop], scope: containerRef }
  );

  // If i cache reload in the middle of the timeline, ScrollTrigger.onEnter() gets run and sets alreadyRecalculated to true while rendering the page, we don't want that - set it to false after page load
  useEffect(() => {
    if (alreadyRecalculated.current === false) return;
    alreadyRecalculated.current = false;
  }, []);
};

export default useGsapTimeline;
